import { useEffect, useState } from "react";
import api, { formatBRL, formatCPF } from "@/lib/api";
import { ClipboardList, Stethoscope, TrendingUp, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";

export default function CredenciadoDashboard() {
  const [data, setData] = useState({ gross: 0, net: 0, count: 0, recent: [] });
  const [form, setForm] = useState({ client_document: "", client_name: "", service: "", amount_gross: "", discount_percent: 30 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/dashboard/credenciado").then(r=>r.data).catch(()=>({gross:0,net:0,count:0,recent:[]}));
    setData(r);
  };
  useEffect(()=>{ load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/atendimentos", {
        ...form,
        amount_gross: parseFloat(form.amount_gross || 0),
        discount_percent: parseFloat(form.discount_percent || 0),
      });
      toast.success("Atendimento registrado!");
      setForm({ client_document: "", client_name: "", service: "", amount_gross: "", discount_percent: 30 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registrar");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="credenciado-dashboard">
      <div className="grid sm:grid-cols-3 gap-5">
        <div className="kpi-tile" data-testid="kpi-atendimentos">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Atendimentos</div>
          <div className="font-display text-3xl font-black text-[#0E2A5C]">{data.count}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1"><ClipboardList className="h-3 w-3" /> registrados</div>
        </div>
        <div className="kpi-tile" data-testid="kpi-bruto">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Faturamento bruto</div>
          <div className="font-display text-3xl font-black text-[#0E2A5C]">{formatBRL(data.gross)}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1"><TrendingUp className="h-3 w-3 text-emerald-500" /> antes do desconto</div>
        </div>
        <div className="kpi-tile" data-testid="kpi-liquido">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Faturamento líquido</div>
          <div className="font-display text-3xl font-black text-[#2A8FE0]">{formatBRL(data.net)}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1"><ArrowDownRight className="h-3 w-3" /> a receber</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 card-soft p-6 space-y-4" data-testid="atendimento-form">
          <h2 className="font-display text-xl font-bold text-[#0E2A5C] flex items-center gap-2"><Stethoscope className="h-5 w-5" /> Registrar atendimento</h2>
          <div>
            <label className="text-sm font-medium">CPF do cliente</label>
            <input required value={formatCPF(form.client_document)} onChange={(e)=>setForm({...form, client_document: e.target.value.replace(/\D/g,"")})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="atendimento-cpf" />
          </div>
          <div>
            <label className="text-sm font-medium">Nome do cliente</label>
            <input required value={form.client_name} onChange={(e)=>setForm({...form, client_name: e.target.value})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="atendimento-nome" />
          </div>
          <div>
            <label className="text-sm font-medium">Serviço prestado</label>
            <input required value={form.service} onChange={(e)=>setForm({...form, service: e.target.value})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="atendimento-servico" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Valor bruto (R$)</label>
              <input required type="number" step="0.01" value={form.amount_gross} onChange={(e)=>setForm({...form, amount_gross: e.target.value})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="atendimento-valor" />
            </div>
            <div>
              <label className="text-sm font-medium">Desconto (%)</label>
              <input required type="number" value={form.discount_percent} onChange={(e)=>setForm({...form, discount_percent: e.target.value})} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="atendimento-desconto" />
            </div>
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="atendimento-submit">
            {busy ? "Registrando..." : "Registrar atendimento"}
          </button>
        </form>

        <div className="lg:col-span-3 card-soft p-6">
          <h2 className="font-display text-xl font-bold text-[#0E2A5C]">Conciliação financeira</h2>
          <p className="text-sm text-slate-500">Últimos atendimentos registrados</p>
          <div className="mt-4 divide-y divide-slate-100" data-testid="atendimentos-list">
            {data.recent.length === 0 && <div className="py-8 text-center text-slate-500">Nenhum atendimento ainda.</div>}
            {data.recent.map(a => (
              <div key={a.id} className="py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`atendimento-${a.id}`}>
                <div>
                  <div className="font-semibold text-[#0E2A5C]">{a.service}</div>
                  <div className="text-xs text-slate-500">{a.client_name} • {formatCPF(a.client_document)} • {new Date(a.created_at).toLocaleDateString("pt-BR")}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500 line-through">{formatBRL(a.amount_gross)}</div>
                  <div className="font-display font-bold text-[#2A8FE0]">{formatBRL(a.amount_net)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
